/**
 * Intake Request Modal Component
 * 
 * Modal for practitioners to send intake questionnaires to patients
 */

import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Send,
  FileText,
  Clock,
  CheckCircle2,
  Sparkles,
  Copy,
  Mail,
  Loader2,
  ExternalLink,
  AlertCircle,
  Stethoscope,
  Lock,
  X
} from 'lucide-react';
import { FHIRPatient } from '@/services/fhirPatientsService';
import { CompletenessResult, TherapyCategory } from '@/lib/dataCompleteness';
import { 
  generateTargetedIntake, 
  generateFullIntake,
  GeneratedIntakeForm 
} from '@/lib/intake';
import {
  initiateIntake,
  InitiateIntakeResponse,
  IntakeStatusItem,
} from '@/services/intakeService';
import { mdiService } from '@/services/mdiService';
import { useClinic } from '@/contexts/ClinicContext';
import { getDefaultContactValue } from '@/lib/utils';

interface IntakeRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patient: FHIRPatient | null;
  assessment: CompletenessResult | null;
  activeIntakes?: IntakeStatusItem[];
  onSendIntake?: (config: IntakeRequestConfig) => Promise<InitiateIntakeResponse>;
  onSuccess?: () => void;
}

export interface IntakeRequestConfig {
  patientId: string;
  patientEmail: string;
  patientName: string;
  intakeType: 'targeted' | 'full';
  therapyCategory: TherapyCategory;
  missingFields: string[];
  providerNotes: string;
  sendEmail: boolean;
}

// Map therapy category to MDI program
const therapyCategoryToMdiProgram: Record<string, string> = {
  trt: 'trt',
  hrt: 'hrt',
  glp1: 'glp1',
  weightwise: 'weightwise',
  peptide: 'general',
  nad: 'general',
  fertility: 'general',
  general: 'general',
};

export const IntakeRequestModal: React.FC<IntakeRequestModalProps> = ({
  open,
  onOpenChange,
  patient,
  assessment,
  activeIntakes = [],
  onSendIntake,
  onSuccess
}) => {
  const { clinic } = useClinic();
  const isGuidedClinic = clinic?.clinic_archetype === 'guided';
  const [intakeType, setIntakeType] = useState<'targeted' | 'full'>(
    assessment?.recommendedIntakeType === 'full' ? 'full' : (assessment ? 'targeted' : 'full')
  );
  const [providerNotes, setProviderNotes] = useState('');
  const [patientEmail, setPatientEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [intakeLink, setIntakeLink] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [selectedTherapyCategory, setSelectedTherapyCategory] = useState<TherapyCategory>('general');
  
  // MDI state
  const [sendToMdi, setSendToMdi] = useState(false);
  const [isMdiEnabled, setIsMdiEnabled] = useState(false);
  const [allowedPrograms, setAllowedPrograms] = useState<string[]>([]);

  const blockedCategories = React.useMemo(() => {
    const blocked = new Set<TherapyCategory>();
    const validCategories = new Set<TherapyCategory>([
      'trt',
      'hrt',
      'glp1',
      'peptide',
      'nad',
      'fertility',
      'general',
    ]);

    for (const intake of activeIntakes) {
      const status = String(intake.status || '').toLowerCase();
      const category = String(intake.therapyCategory || '').toLowerCase() as TherapyCategory;
      if ((status === 'pending' || status === 'started') && validCategories.has(category)) {
        blocked.add(category);
      }
    }

    return blocked;
  }, [activeIntakes]);

  const isSelectedTherapyBlocked = blockedCategories.has(selectedTherapyCategory);

  // Fetch MDI settings when modal opens
  useEffect(() => {
    const fetchMdiSettings = async () => {
      if (!open || !clinic?.id) return;
      
      try {
        const response = await mdiService.getSettings(clinic.id);
        if (response.success && response.data) {
          const enabled = response.data.mdi_enabled === true || response.data.enabled === true;
          setIsMdiEnabled(enabled);
          // Backend returns allowed_programs (snake_case), handle both cases
          const programs = (response.data as any).allowed_programs || response.data.allowedPrograms || [];
          setAllowedPrograms(programs);
        }
      } catch (error) {
        console.error('Error fetching MDI settings:', error);
        setIsMdiEnabled(false);
        setAllowedPrograms([]);
      }
    };
    
    fetchMdiSettings();
  }, [open, clinic?.id]);

  // Check if the selected therapy category is eligible for MDI
  const isMdiEligible = React.useMemo(() => {
    if (!isMdiEnabled) return false;
    
    const mdiProgram = therapyCategoryToMdiProgram[selectedTherapyCategory];
    if (!mdiProgram) return false;
    
    // If no allowed programs specified, all programs are allowed
    if (allowedPrograms.length === 0) return true;
    
    return allowedPrograms.includes(mdiProgram);
  }, [isMdiEnabled, selectedTherapyCategory, allowedPrograms]);

  // Reset sendToMdi when therapy category changes and is not eligible
  useEffect(() => {
    if (!isMdiEligible) {
      setSendToMdi(false);
    }
  }, [isMdiEligible]);

  // Prefill patient email when modal opens or patient changes (respect is_default in contacts)
  useEffect(() => {
    if (open && patient) {
      const email =
        patient.email ||
        getDefaultContactValue((patient as { contacts?: { key?: string; value?: string; is_default?: boolean }[] })?.contacts, 'email');
      if (email) setPatientEmail(email);
    }
  }, [open, patient]);

  // Reset states when modal opens
  useEffect(() => {
    if (open) {
      setIntakeType(assessment?.recommendedIntakeType === 'full' ? 'full' : (assessment ? 'targeted' : 'full'));
    }
  }, [open, assessment]);

  // Generate intake form preview
  const generatedIntake: GeneratedIntakeForm | null = React.useMemo(() => {
    if (intakeType === 'full') {
      return generateFullIntake(selectedTherapyCategory, patient?.gender);
    } else if (assessment) {
      return generateTargetedIntake(
        assessment.missingRequired,
        selectedTherapyCategory,
        patient?.gender
      );
    }
    return null;
  }, [assessment, intakeType, patient?.gender, selectedTherapyCategory]);

  // Therapy category display names
  const therapyLabels: Record<TherapyCategory, string> = {
    trt: "Men's Health (TRT)",
    hrt: "Women's Health (HRT)",
    glp1: 'Weight Management (GLP-1)',
    peptide: 'Peptide Therapy',
    nad: 'NAD+ Therapy',
    fertility: 'Fertility',
    general: 'General Health'
  };

  const handleSend = async () => {
    if (!patient) return;
    
    // Use the manually selected therapy category
    const therapyCategory = selectedTherapyCategory;

    if (isSelectedTherapyBlocked) {
      setError(
        `${therapyLabels[therapyCategory]} intake is already pending/in progress. ` +
          'Complete it before sending a new one.',
      );
      return;
    }
    
    setIsSending(true);
    setError(null);
    
    try {
      // Call the real API directly
      // Use full_name from Patient table (includes middle name) or fallback to first + last
      const patientFullName = patient.full_name || `${patient.first_name} ${patient.last_name}`.trim();
      
      // Serialize missing fields from the data completeness assessment
      const missingFields = assessment
        ? [...assessment.missingRequired, ...assessment.missingRecommended].map(mf => ({
            fieldId: mf.field.id,
            fieldName: mf.field.name,
            priority: mf.field.priority,
            category: mf.field.category,
          }))
        : undefined;

      const response = await initiateIntake({
        patientId: patient.id,
        clinicId: clinic?.id,
        intakeType,
        therapyCategory,
        missingFields: intakeType === 'targeted' ? missingFields : undefined,
        patientEmail: patientEmail || undefined,
        patientName: patientFullName,
        providerNotes: providerNotes || undefined,
        sendEmail: !!patientEmail,
        sendToMdi: isMdiEligible && (sendToMdi || (isGuidedClinic && selectedTherapyCategory === 'glp1')),
      });

      if (response.success && response.data) {
        setSendSuccess(true);
        setIntakeLink(response.data.intakeLink);
        setEmailSent(response.data.emailSent);
        
        // Log any email errors (but don't fail the operation)
        if (response.data.emailError) {
          console.warn('Email sending had an issue:', response.data.emailError);
        }
      } else {
        setSendSuccess(false);
        setError(response.message || 'Unable to send intake right now. Please try again.');
      }
    } catch (err: any) {
      console.error('Error sending intake:', err);
      setSendSuccess(false);
      setError(err?.message || 'Unable to send intake right now. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleCopyLink = () => {
    if (intakeLink) {
      navigator.clipboard.writeText(intakeLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const resetState = () => {
    setSendSuccess(false);
    setIntakeLink(null);
    setProviderNotes('');
    setPatientEmail('');
    setEmailSent(false);
    setError(null);
    setCopied(false);
    setSelectedTherapyCategory('general');
    setSendToMdi(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetState();
      onOpenChange(false);
    }
  };

  const handleDone = () => {
    // Refresh the intake list when user clicks Done
    if (onSuccess) {
      onSuccess();
    }
    resetState();
    onOpenChange(false);
  };

  if (!patient) return null;

  // Use the manually selected therapy category
  const currentTherapyCategory = selectedTherapyCategory;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent hideCloseButton className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0" style={{ borderRadius: '0.75rem' }}>
        {/* Header */}
        <div className="px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2 text-lg font-montserrat">
                <Send className="h-5 w-5 text-brand-teal" />
                Send Intake Form
              </DialogTitle>
              <DialogDescription className="mt-1">
                Send an intake questionnaire to {patient.full_name || `${patient.first_name} ${patient.last_name}`.trim()}
              </DialogDescription>
            </div>
            <button
              onClick={() => handleOpenChange(false)}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-muted border border-border text-muted-foreground transition-colors hover:bg-muted/70 flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {!sendSuccess ? (
          <>
            {/* Scrollable Form Content */}
            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
              {/* Patient Info */}
              <div className="bg-muted/50 rounded-lg p-4 border">
                <p className="text-sm font-medium text-foreground">
                  {patient.full_name || `${patient.first_name} ${patient.last_name}`.trim()}
                </p>
                {(patient.age || patient.gender_text) && (
                  <p className="text-xs text-muted-foreground">
                    {patient.age ? `${patient.age} years old` : ''}{patient.age && patient.gender_text ? ' • ' : ''}{patient.gender_text || ''}
                  </p>
                )}
              </div>

              {/* Treatment Type Selection */}
              <div className="space-y-3">
                <Label>Treatment Type</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {(Object.keys(therapyLabels) as TherapyCategory[])
                    .filter((category) => {
                      if (category === 'hrt' && patient?.gender === 'male') return false;
                      if (category === 'trt' && patient?.gender === 'female') return false;
                      return true;
                    })
                    .map((category) => {
                      const isBlocked = blockedCategories.has(category);
                      return (
                        <button
                          key={category}
                          onClick={() => setSelectedTherapyCategory(category)}
                          className={`relative p-3 rounded-lg border text-left transition-all ${
                            selectedTherapyCategory === category
                              ? 'border-brand-teal bg-brand-teal/5'
                              : 'border-border bg-muted/30 hover:border-muted-foreground/30'
                          } ${isBlocked ? 'opacity-70' : ''}`}
                          title={isBlocked ? 'An intake for this category is already pending/in progress' : undefined}
                        >
                          {isBlocked && (
                            <Badge variant="outline" className="absolute -top-2 right-2 text-[10px] px-1.5 py-0 text-amber-700 border-amber-300 bg-amber-50">
                              <Lock className="h-3 w-3 mr-1" />
                              In Progress
                            </Badge>
                          )}
                          <span className={`text-sm font-medium ${selectedTherapyCategory === category ? 'text-brand-teal' : 'text-foreground'}`}>
                            {therapyLabels[category]}
                          </span>
                        </button>
                      );
                    })}
                </div>
              </div>

              {isSelectedTherapyBlocked && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">
                    {therapyLabels[selectedTherapyCategory]} intake is currently pending/in progress for this patient.
                    Complete it before sending a new intake for the same treatment type.
                  </p>
                </div>
              )}

              {/* Intake Type Selection (only when assessment is available) */}
              {assessment && (
                <div className="space-y-3">
                  <Label>Form Type</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setIntakeType('targeted')}
                      className={`p-4 rounded-lg border text-left transition-all ${
                        intakeType === 'targeted'
                          ? 'border-brand-teal bg-brand-teal/5'
                          : 'border-border bg-muted/30 hover:border-muted-foreground/30'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className={`h-4 w-4 ${intakeType === 'targeted' ? 'text-brand-teal' : 'text-muted-foreground'}`} />
                        <span className="font-medium text-foreground">Quick Form</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Only missing required data ({assessment.missingRequired.length} items)
                      </p>
                      <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>~{Math.max(2, assessment.missingRequired.length)} min</span>
                      </div>
                    </button>

                    <button
                      onClick={() => setIntakeType('full')}
                      className={`p-4 rounded-lg border text-left transition-all ${
                        intakeType === 'full'
                          ? 'border-brand-teal bg-brand-teal/5'
                          : 'border-border bg-muted/30 hover:border-muted-foreground/30'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className={`h-4 w-4 ${intakeType === 'full' ? 'text-brand-teal' : 'text-muted-foreground'}`} />
                        <span className="font-medium text-foreground">Complete Profile</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Complete questionnaire for {therapyLabels[selectedTherapyCategory]}
                      </p>
                      <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>~10-15 min</span>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Intake Preview */}
              {generatedIntake && (
                <div className="space-y-2">
                  <Label>Questionnaire Sections</Label>
                  <div className="bg-muted/30 rounded-lg border p-3 max-h-40 overflow-y-auto">
                    <div className="space-y-2">
                      {generatedIntake.sections.map((section, idx) => (
                        <div key={idx} className="flex items-center justify-between py-1">
                          <span className="text-sm text-foreground">{section.title}</span>
                          <Badge variant="outline" className="text-xs">
                            {section.questions.length} questions
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Patient Email */}
              <div className="space-y-2">
                <Label htmlFor="patient-email">
                  Patient Email (optional)
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="patient-email"
                    type="email"
                    value={patientEmail}
                    onChange={(e) => setPatientEmail(e.target.value)}
                    placeholder="patient@example.com"
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Leave blank to copy the link manually instead of emailing
                </p>
              </div>

              {/* Provider Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">
                  Notes for Patient (optional)
                </Label>
                <Textarea
                  id="notes"
                  value={providerNotes}
                  onChange={(e) => setProviderNotes(e.target.value)}
                  placeholder="Add any specific instructions or context for the patient..."
                  className="min-h-[80px]"
                />
              </div>

              {/* Send to MDI Checkbox - hidden in guided clinics; GLP intakes are auto-sent to MDI there */}
              {isMdiEligible && !isGuidedClinic && (
                <div className="flex items-start gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <Checkbox
                    id="send-to-mdi"
                    checked={sendToMdi}
                    onCheckedChange={(checked) => setSendToMdi(checked === true)}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <label
                      htmlFor="send-to-mdi"
                      className="text-sm font-medium text-indigo-900 cursor-pointer flex items-center gap-2"
                    >
                      <Stethoscope className="h-4 w-4 text-indigo-600" />
                      Send to MDI Telehealth
                    </label>
                    <p className="text-xs text-indigo-700 mt-0.5">
                      Automatically submit for async telehealth review when patient completes the intake
                    </p>
                  </div>
                </div>
              )}

              {/* Info message for data collection */}
              {assessment.status === 'insufficient' && (
                <div className="flex items-start gap-2 p-3 bg-brand-copper/5 border border-brand-copper/20 rounded-lg">
                  <FileText className="h-4 w-4 text-brand-copper flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-brand-copper">
                    This intake will provide IntelliHealth with the data needed for personalized recommendations.
                  </p>
                </div>
              )}

              {/* Error state */}
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">{error}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex-shrink-0 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSend}
                disabled={isSending || isSelectedTherapyBlocked}
                className="bg-brand-teal hover:bg-brand-teal/90 text-white font-medium"
              >
                {isSending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    {patientEmail ? 'Send Form' : 'Generate Link'}
                  </>
                )}
              </Button>
            </div>
          </>
        ) : (
          /* Success State */
          <div className="py-6 px-6 text-center flex-1 overflow-y-auto">
            <div className="w-16 h-16 bg-brand-teal/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-brand-teal" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2 font-montserrat">
              {emailSent ? 'Email Sent!' : 'Form Ready!'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {emailSent
                ? `An email has been sent to ${patientEmail}.`
                : 'The form link is ready to share with your patient.'}
            </p>

            {/* Warning if API failed */}
            {error && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4 text-left">
                <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">{error}</p>
              </div>
            )}

            {intakeLink && (
              <div className="bg-muted/50 rounded-lg p-4 mb-4 border">
                <Label className="text-muted-foreground text-xs mb-2 block">Intake Link</Label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={intakeLink}
                    className="text-sm"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCopyLink}
                    title={copied ? 'Copied!' : 'Copy link'}
                  >
                    {copied ? <CheckCircle2 className="h-4 w-4 text-brand-teal" /> : <Copy className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => window.open(intakeLink, '_blank')}
                    title="Open in new tab"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
                {copied && (
                  <p className="text-xs text-brand-teal mt-2">Link copied to clipboard!</p>
                )}
              </div>
            )}

            <Button
              onClick={handleDone}
              className="bg-brand-teal hover:bg-brand-teal/90 text-white font-medium"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default IntakeRequestModal;
